import { Reveal } from "@/components/motion";
import { usePageMeta } from "@/lib/seo";
import { CONTACT_EMAIL } from "@/data/content";

const LegalLayout = ({ title, updated, children, testId }) => (
  <section className="pt-36 lg:pt-44" data-testid={testId}>
    <div className="mx-auto max-w-3xl px-6 pb-24 lg:pb-32">
      <Reveal>
        <div className="flex items-center gap-4">
          <span className="h-px w-10 bg-gold" />
          <span className="text-xs font-medium uppercase tracking-[0.3em] text-charcoal/60">
            Legal
          </span>
        </div>
        <h1 className="mt-8 font-heading text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
          {title}
        </h1>
        <p className="mt-4 text-xs uppercase tracking-[0.2em] text-charcoal/45">
          Last updated: {updated}
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="mt-12 space-y-8 text-sm leading-relaxed text-charcoal/75">
          {children}
        </div>
      </Reveal>
    </div>
  </section>
);

const Block = ({ heading, children }) => (
  <div>
    <h2 className="font-heading text-lg font-bold tracking-tight text-navy">
      {heading}
    </h2>
    <p className="mt-3">{children}</p>
  </div>
);

export const PrivacyPolicy = () => {
  usePageMeta(
    "Privacy Policy — Iconicho, LLC",
    "Privacy Policy for Iconicho, LLC — how information submitted through this website is collected and used."
  );
  return (
    <LegalLayout title="Privacy Policy" updated="July 2026" testId="privacy-page">
      <Block heading="Information We Collect">
        When you submit an inquiry through this website, we collect the
        information you provide — such as your name, company or organization,
        email address, phone number, inquiry type, and message.
      </Block>
      <Block heading="How We Use Information">
        Information submitted through this website is used solely to respond to
        your inquiry, coordinate requested services, and maintain business
        correspondence with you.
      </Block>
      <Block heading="Sharing of Information">
        Iconicho, LLC does not sell, rent, or share your personal information
        with third parties for their marketing purposes. Information may be
        processed by service providers that support the operation of this
        website and our business communications.
      </Block>
      <Block heading="Data Retention">
        Inquiry records are retained as needed to manage business relationships
        and meet operational or legal requirements.
      </Block>
      <Block heading="Contact">
        Questions about this policy may be directed to{" "}
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="border-b border-gold/50 text-navy transition-colors duration-300 hover:border-gold"
        >
          {CONTACT_EMAIL}
        </a>
        .
      </Block>
    </LegalLayout>
  );
};

export const TermsConditions = () => {
  usePageMeta(
    "Terms & Conditions — Iconicho, LLC",
    "Terms and Conditions governing the use of the Iconicho, LLC corporate website."
  );
  return (
    <LegalLayout title="Terms & Conditions" updated="July 2026" testId="terms-page">
      <Block heading="Use of This Website">
        This website is provided by Iconicho, LLC for general corporate
        information and business inquiry purposes. By using this website, you
        agree to these terms.
      </Block>
      <Block heading="No Offer or Guarantee">
        Content on this website describes the general capabilities and service
        categories of Iconicho, LLC. It does not constitute an offer, a
        guarantee of service availability, or a commitment to any contract.
        Engagements are formed only through executed agreements.
      </Block>
      <Block heading="Intellectual Property">
        All content, branding, and design on this website are the property of
        Iconicho, LLC and may not be reproduced without written permission.
      </Block>
      <Block heading="Limitation of Liability">
        This website is provided as is. Iconicho, LLC is not liable for any
        damages arising from the use of, or inability to use, this website or
        its content.
      </Block>
      <Block heading="Contact">
        Questions about these terms may be directed to{" "}
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="border-b border-gold/50 text-navy transition-colors duration-300 hover:border-gold"
        >
          {CONTACT_EMAIL}
        </a>
        .
      </Block>
    </LegalLayout>
  );
};
